import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Input, Button, Select, Spin, Upload, Modal, message } from "antd";
import { LoadingOutlined, CameraOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { fetchData, profileData } from "../../../slices/profileSlice";
import { useNavigate } from "react-router-dom";
import TopNav from "../../../components/top-nav";
import useLocationData from "../../../hooks/useLocationData";
import AutoCompleteAddress from "../../../components/AutoCompleteAddress";
import {
  _onboard_biker,
  _get_profile,
  _upload_any_file,
} from "../../../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../common/utils/notification";
import { PinActivation } from "../../../components/PinActivation";

const { Option } = Select;

const schema = yup.object().shape({
  experience: yup.string().required("Years of delivery experience is required"),
  homeAddress: yup.string().required("Home address is required"),
  state: yup.string().required("State is required"),
  lga: yup.string().required("Location is required"),
  deliveryMode: yup.string().required("Delivery mode is required"),
  nin: yup
    .string()
    .matches(/^\d{11}$/, "NIN must be exactly 11 digits")
    .required("NIN is required"),
  preferredArea: yup.string().required("Preferred delivery area is required"),
});

const ALLOWED_TYPES = {
  driversLicenceUrl: ["image/jpeg", "image/png", "application/pdf"],
  recentPictureUrl: ["image/jpeg", "image/png"],
  videoClipUrl: ["video/mp4", "video/quicktime", "video/x-msvideo"],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const JoinAsBiker = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [recentPictureFile, setRecentPictureFile] = useState([]);
  const [driversLicenceFile, setDriversLicenceFile] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(null);
  const [videoClipFile, setVideoClipFile] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({
    driversLicenceUrl: false,
    recentPictureUrl: false,
    videoClipUrl: false,
  });
  const [uploadedUrls, setUploadedUrls] = useState({
    driversLicenceUrl: null,
    recentPictureUrl: null,
    videoClipUrl: null,
  });
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [onboardingErrorMessage, setOnboardingErrorMessage] = useState("");

  const {
    allStates,
    selectedState,
    setSelectedState,
    allLgas,
    loadingStates,
    loadingLgas,
  } = useLocationData();

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const watchedState = watch("state");
  const watchedDeliveryMode = watch("deliveryMode");
  const requiresLicense = watchedDeliveryMode && ["Van", "Car", "Bike"].includes(watchedDeliveryMode);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profileResponse = await _get_profile();
        const user = profileResponse.data.data;
        setUserProfile(user);
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    // Check if user has already seen the disclaimer modal
    const hasSeenDisclaimer = localStorage.getItem("biker_disclaimer_seen");
    if (!hasSeenDisclaimer) {
      setShowDisclaimerModal(true);
    }
  }, []);

  useEffect(() => {
    if (watchedState) {
      setSelectedState(watchedState);
      setValue("lga", undefined);
    }
  }, [watchedState, setSelectedState, setValue]);

  useEffect(() => {
    if (!requiresLicense) {
      setDriversLicenceFile([]);
      setUploadedUrls(prev => ({
        ...prev,
        driversLicenceUrl: null
      }));
    }
  }, [requiresLicense]);

  const validateFile = (file, fileType) => {
    if (file.size > MAX_FILE_SIZE) {
      showErrorNotification(
        `File must be smaller than 10MB. File size: ${(
          file.size /
          1024 /
          1024
        ).toFixed(2)}MB`
      );
      return false;
    }

    if (!ALLOWED_TYPES[fileType]?.includes(file.type)) {
      const allowedFormats = ALLOWED_TYPES[fileType].join(", ");
      showErrorNotification(
        `Invalid file type. Allowed formats: ${allowedFormats}`
      );
      return false;
    }

    return true;
  };

  const uploadFile = async (file, fileType, setFileList) => {
    if (!validateFile(file, fileType)) {
      return;
    }

    setUploadingFiles((prev) => ({
      ...prev,
      [fileType]: true,
    }));

    try {
      const response = await _upload_any_file(file);
      const fileUrl = response?.data?.data?.url || response.data;

      setUploadedUrls((prev) => ({
        ...prev,
        [fileType]: fileUrl,
      }));

      const preview = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });

      setFileList([
        {
          uid: "-1",
          name: file.name,
          status: "done",
          url: preview,
          thumbUrl: preview,
        },
      ]);

      showSuccessNotification("File uploaded successfully");
    } catch (error) {
      showErrorNotification(error.response?.data?.message || "Upload failed");
      setFileList([]);
    } finally {
      setUploadingFiles((prev) => ({
        ...prev,
        [fileType]: false,
      }));
    }
  };

  const onFinish = async (data) => {
    setSubmitting(true);
    try {
      // if (!uploadedUrls.driversLicenceUrl) {
      //   showErrorNotification("Please upload your driver's licence");
      //   setSubmitting(false);
      //   return;
      // }
      // if (!uploadedUrls.recentPictureUrl) {
      //   showErrorNotification("Please upload a recent picture");
      //   setSubmitting(false);
      //   return;
      // }
      // if (!uploadedUrls.videoClipUrl) {
      //   showErrorNotification("Please upload a video clip");
      //   setSubmitting(false);
      //   return;
      // }

      const payload = {
        bikerType: "KONNECT",
        experience: data.experience,
        homeAddress: data.homeAddress,
        deliveryMode:
          data.deliveryMode === "Bike"
            ? "motorcycle"
            : data.deliveryMode === "Bicycle"
              ? "bicycle"
              : data.deliveryMode === "Van"
                ? "scooter"
                : data.deliveryMode === "Car"
                  ? "scooter"
                  : "motorcycle",
        nin: data.nin,
        driversLicenceUrl: uploadedUrls.driversLicenceUrl,
        recentPictureUrl: uploadedUrls.recentPictureUrl,
        videoClipUrl: uploadedUrls.videoClipUrl,
        state: data.state,
        lga: data.lga,
        detailAddress: data.detailAddress,
        preferredArea: data.preferredArea,
        localArea: `${data.state}, ${data.lga}`,
        isAvailable: true,
      };

      await _onboard_biker(payload);
      dispatch(profileData(false));
      dispatch(fetchData(false));
      setShowSuccessModal(true);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.message?.includes("verified users")) {
        setOnboardingErrorMessage(err.response.data.message);
        setShowPinModal(true);
      } else {
        showErrorNotification(err.response?.data?.message || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const antIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#fff" }} spin />
  );

  const createUploadProps = (fileType, setFileList) => ({
    multiple: false,
    maxCount: 1,
    beforeUpload: () => false,
    onChange: ({ fileList }) => {
      if (fileList.length > 0) {
        const file = fileList[0].originFileObj || fileList[0];
        uploadFile(file, fileType, setFileList);
      }
    },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        uploadFile(files[0], fileType, setFileList);
      }
    },
  });

  const renderUploadBox = (fileList, isUploading) => {
    if (fileList?.length > 0 && (fileList[0].url || fileList[0].thumbUrl)) {
      const file = fileList[0];
      const fileUrl = file.url || file.thumbUrl;
      const isVideo = file.name?.toLowerCase().match(/\.(mp4|mov|avi)$/i);

      return (
        <div className="relative w-full h-full flex items-center justify-center bg-gray-50 rounded-lg overflow-hidden">
          {isVideo ? (
            <video
              src={fileUrl}
              className="w-full h-full object-contain rounded-lg"
              controls
              style={{ maxWidth: "100%", maxHeight: "100%" }}
            />
          ) : (
            <img
              src={fileUrl}
              alt={file.name}
              className="w-full h-full object-contain rounded-lg"
              style={{ maxWidth: "100%", maxHeight: "100%" }}
            />
          )}
        </div>
      );
    }

    if (isUploading) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <Spin size="large" />
          <div className="mt-2  text-center">Uploading...</div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <CameraOutlined className="text-2xl mb-2" />
        <div className="">Upload</div>
      </div>
    );
  };

  return (
    <>
      <TopNav title="Join as a Biker" />
      <div className="display-main-content mt_2">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Welcome to Konnect Delivery
            </h1>
            <p className="text-gray-600 text-xl max-w-md mx-auto">
              Note, please complete the form below to join our dedicated
              delivery team
            </p>
          </div>

          <form onSubmit={handleSubmit(onFinish)} className="space-y-6">
            <div>
              <label className="block  font-medium text-gray-700 mb-2">
                Years of delivery experience
              </label>
              <Controller
                name="experience"
                control={control}
                render={({ field }) => (
                  <Input {...field} type="number"
                    inputMode="numeric" placeholder="e.g. 2 years" />
                )}
              />
              {errors.experience && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.experience.message}
                </p>
              )}
            </div>

            <div>
              <label className="block  font-medium text-gray-700 mb-2">
                Home address
              </label>
              <Controller
                name="homeAddress"
                control={control}
                render={({ field }) => (
                  <AutoCompleteAddress
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Enter your home address"
                  />
                )}
              />
              {errors.homeAddress && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.homeAddress.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 gap-6">
              <div>
                <label className="block  font-medium text-gray-700 mb-2">
                  State
                </label>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select state"
                      className="w-full"
                      loading={loadingStates}
                    >
                      {allStates.map((state) => (
                        <Option key={state?.id} value={state?.name}>
                          {state?.name}
                        </Option>
                      ))}
                    </Select>
                  )}
                />
                {errors.state && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.state.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block  font-medium text-gray-700 mb-2">
                  Local Area
                </label>
                <Controller
                  name="lga"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select Location"
                      className="w-full"
                      loading={loadingLgas}
                    >
                      {allLgas.map((lga) => (
                        <Option key={lga?.id} value={lga?.name}>
                          {lga?.name}
                        </Option>
                      ))}
                    </Select>
                  )}
                />
                {errors.lga && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.lga.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block  font-medium text-gray-700 mb-2">
                Choose your preferred delivery area
              </label>
              <Controller
                name="preferredArea"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select Location"
                    className="w-full"
                    loading={loadingLgas}
                  >
                    {allLgas.map((lga) => (
                      <Option key={lga?.id} value={lga?.name}>
                        {lga?.name}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.preferredArea && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.preferredArea.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block  font-medium text-gray-700 mb-2">
                  Your delivery mode (drivers licence required ex Bicycle only)
                </label>
                <Controller
                  name="deliveryMode"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select delivery mode"
                      className="w-full"
                    >
                      <Option value="Van">Van</Option>
                      <Option value="Car">Car</Option>
                      <Option value="Bike">Bike</Option>
                      <Option value="Bicycle">Bicycle</Option>
                    </Select>
                  )}
                />
                {errors.deliveryMode && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.deliveryMode.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block  font-medium text-gray-700 mb-2">
                  NIN
                </label>
                <Controller
                  name="nin"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter your NIN"
                      maxLength={11}
                    />
                  )}
                />
                {errors.nin && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.nin.message}
                  </p>
                )}
              </div>
            </div>

            <div className={`grid gap-6 ${requiresLicense ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {requiresLicense && (
                <div>
                  <label className="block  font-medium text-gray-700 mb-2">
                    Upload driver's license
                  </label>
                  <div
                    className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg grid place-content-center overflow-hidden hover:border-gray-900 hover:bg-gray-50 transition cursor-pointer"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add(
                        "border-gray-900",
                        "bg-gray-50"
                      );
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove(
                        "border-gray-900",
                        "bg-gray-50"
                      );
                    }}
                  >
                    <Upload
                      {...createUploadProps(
                        "driversLicenceUrl",
                        setDriversLicenceFile
                      )}
                      fileList={driversLicenceFile}
                      showUploadList={false}
                      className="w-full h-full"
                    >
                      <div className="w-full h-full flex items-center justify-center">
                        {renderUploadBox(
                          driversLicenceFile,
                          uploadingFiles.driversLicenceUrl
                        )}
                      </div>
                    </Upload>
                  </div>
                </div>
              )}

              <div>
                <label className="block  font-medium text-gray-700 mb-2">
                  Upload a recent picture
                </label>
                <div
                  className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg grid place-content-center overflow-hidden hover:border-gray-900 hover:bg-gray-50 transition cursor-pointer"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add(
                      "border-gray-900",
                      "bg-gray-50"
                    );
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove(
                      "border-gray-900",
                      "bg-gray-50"
                    );
                  }}
                >
                  <Upload
                    {...createUploadProps(
                      "recentPictureUrl",
                      setRecentPictureFile
                    )}
                    fileList={recentPictureFile}
                    showUploadList={false}
                    className="w-full h-full"
                  >
                    <div className="w-full h-full flex items-center justify-center">
                      {renderUploadBox(
                        recentPictureFile,
                        uploadingFiles.recentPictureUrl
                      )}
                    </div>
                  </Upload>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                className="w-full !bg-gray-900 hover:bg-gray-800"
              >
                {submitting ? <Spin indicator={antIcon} /> : "SUBMIT"}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <Modal
        title={null}
        open={showSuccessModal}
        footer={null}
        centered
        closable={false}
        className="success-modal"
      >
        <div className="text-center py-8">
          <div className="text-2xl font-bold text-gray-900 mb-4">
            {userProfile?.firstName}, our team will reach out to you within 2
            days to help you join our delivery team
          </div>
          <Button
            type="primary"
            onClick={() => navigate("/home")}
            className="bg-gray-900 hover:bg-gray-800 mt-4"
          >
            Continue
          </Button>
        </div>
      </Modal>

      <Modal
        title=""
        open={showDisclaimerModal}
        footer={null}
        centered
        closable={false}
        className="disclaimer-modal"
      >
        <div className="py-6">
          <div className="text-center mb-6">
            <p className=" text-gray-700 mb-4">
              This process is to join our dedicated konnect delivery team. It is
              not for the following:
            </p>
            <ul className="text-left list-decimal list-inside space-y-2 text-gray-600 mb-6">
              <li>Business that wants to sell on konnect</li>
              <li>Bikers that are from restaurants</li>
              <li>Bikers from third party delivery organisation</li>
            </ul>
          </div>
          <div className="text-center">
            <Button
              type="primary"
              onClick={() => {
                localStorage.setItem("biker_disclaimer_seen", "true");
                setShowDisclaimerModal(false);
              }}
              className="bg-gray-900 hover:bg-gray-800 px-8 py-2 h-auto"
            >
              Continue
            </Button>
          </div>
        </div>
      </Modal>

      <PinActivation
        open={showPinModal}
        toClose={() => setShowPinModal(false)}
        accountId={userProfile?.accountId}
        errorMessage={onboardingErrorMessage}
      />
    </>
  );
};

export default JoinAsBiker;
